// frontend/src/components/SalesTransactionList.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { 
  Search, Filter, Download, Eye, Edit, Trash2, 
  Plus, Package, Warehouse, DollarSign, Calendar, 
  User, FileText, Hash, RefreshCw, Building,
  ShoppingBag, Truck, CreditCard, CheckCircle, AlertCircle,
  Loader2, X, ChevronDown, Globe, ArrowLeftRight,
  Wallet, Smartphone, ChevronLeft, ChevronRight, Users
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
// import "./SalesTransactionList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const SalesTransactionList = () => {
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filter, setFilter] = useState({
    status: "",
    payment_status: "",
    customer_id: "",
    warehouse_id: "",
    start_date: "",
    end_date: "",
    is_return: ""
  });
  
  const [customers, setCustomers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [stats, setStats] = useState({
    totalTransactions: 0,
    totalQuantity: 0,
    totalPrice: 0,
    avgUnitPrice: 0,
    process: 0,
    pending: 0,
    completed: 0,
    returned: 0,
    saleCount: 0,
    returnCount: 0
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  const isCompanyAdmin = userType === 'company' && currentUser?.role === 'admin';

  useEffect(() => {
    fetchData();
    fetchDropdowns();
    
    if (isSystemAdmin) {
      fetchCompanies();
    }
  }, [selectedCompany]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams();
      if (filter.status) queryParams.append("status", filter.status);
      if (filter.payment_status) queryParams.append("payment_status", filter.payment_status);
      if (filter.customer_id) queryParams.append("customer_id", filter.customer_id);
      if (filter.warehouse_id) queryParams.append("warehouse_id", filter.warehouse_id);
      if (filter.start_date) queryParams.append("start_date", filter.start_date);
      if (filter.end_date) queryParams.append("end_date", filter.end_date);
      if (filter.is_return !== "") queryParams.append("is_return", filter.is_return);
      if (isSystemAdmin && selectedCompany) queryParams.append("company_id", selectedCompany);
      
      const queryString = queryParams.toString();
      const apiUrl = `${url}/api/sales${queryString ? `?${queryString}` : ""}`;
      
      const response = await axios.get(apiUrl);

      if (response.data.success) {
        setTransactions(response.data.data);
        setStats(response.data.stats || {});
      }
    } catch (error) {
      toast.error("Failed to load sales transactions");
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const params = {};
      if (isSystemAdmin && selectedCompany) {
        params.company_id = selectedCompany;
      }
      
      const [customersRes, warehousesRes] = await Promise.all([
        axios.get(`${url}/api/customer/dropdown`, { params }),
        axios.get(`${url}/api/warehouse/dropdown`, { params })
      ]);

      if (customersRes.data.success) setCustomers(customersRes.data.data || []);
      if (warehousesRes.data.success) setWarehouses(warehousesRes.data.data || []);
    } catch (error) {
      // Silent fail
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${url}/api/company`);
      
      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      // Silent fail
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchData();
  };

  const resetFilters = () => {
    setFilter({
      status: "",
      payment_status: "",
      customer_id: "",
      warehouse_id: "",
      start_date: "",
      end_date: "",
      is_return: ""
    });
    setSelectedCompany("");
    setSearchTerm("");
    setCurrentPage(1);
    fetchData();
  };

  const handleDelete = async (id, referenceId) => {
    if (!window.confirm(`Are you sure you want to delete transaction "${referenceId}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${url}/api/sales/${id}`);

      if (response.data.success) {
        toast.success(`Transaction "${referenceId}" cancelled successfully`);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete transaction");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return "Invalid date";
    }
  };

  const getProductInfo = (transaction) => {
    if (transaction.product_id) {
      if (typeof transaction.product_id === 'object') {
        return {
          name: transaction.product_id.product_name || "Unknown Product",
          code: transaction.product_id.product_code || 
                transaction.product_id.product_no || 
                "N/A",
          product_no: transaction.product_id.product_no || "N/A"
        };
      }
    }
    return { name: "Unknown Product", code: "N/A", product_no: "N/A" };
  };

  const getWarehouseInfo = (transaction) => {
    if (transaction.warehouse_id) {
      if (typeof transaction.warehouse_id === 'object') {
        return {
          name: transaction.warehouse_id.warehouse_name || "Unknown Warehouse",
          code: transaction.warehouse_id.warehouse_code || "N/A"
        };
      }
    }
    return { name: "Unknown Warehouse", code: "N/A" };
  };

  const getCustomerInfo = (transaction) => {
    if (transaction.customer_id) {
      if (typeof transaction.customer_id === 'object') {
        return {
          name: transaction.customer_id.customer_name || "Unknown Customer",
          code: transaction.customer_id.customer_code || "N/A"
        };
      }
    }
    return { name: "Unknown Customer", code: "N/A" };
  };

  const getTransactionTypeInfo = (transaction) => {
    if (transaction.transaction_type_id) {
      if (typeof transaction.transaction_type_id === 'object') {
        return {
          type: transaction.transaction_type_id.transaction_type || "N/A",
          sign: transaction.transaction_type_id.sign || "-"
        };
      }
    }
    return { type: "N/A", sign: "-" };
  };

  const getPaymentMethodIcon = (method) => {
    switch(method) {
      case 'cash': return <CreditCard size={12} />;
      case 'evc': return <Smartphone size={12} />;
      case 'edahab': return <Wallet size={12} />;
      case 'bank_transfer': return <Truck size={12} />;
      default: return <DollarSign size={12} />;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: '#fff3cd', text: '#856404', label: 'Pending' },
      process: { bg: '#cce5ff', text: '#004085', label: 'Process' },
      completed: { bg: '#d4edda', text: '#155724', label: 'Completed' },
      cancelled: { bg: '#f8d7da', text: '#721c24', label: 'Cancelled' },
      returned: { bg: '#e2d5f1', text: '#4a1c6c', label: 'Returned' }
    };
    
    const config = statusConfig[status] || { bg: '#e2e3e5', text: '#383d41', label: status };
    
    return (
      <span className={`status-badge status-${status}`} style={{
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 600,
        backgroundColor: config.bg,
        color: config.text
      }}>
        {config.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: '#fff3cd', text: '#856404', label: 'Pending' },
      paid: { bg: '#d4edda', text: '#155724', label: 'Paid' },
      partial: { bg: '#cce5ff', text: '#004085', label: 'Partial' },
      notpaid: { bg: '#f8d7da', text: '#721c24', label: 'Not Paid' },
      overdue: { bg: '#f8d7da', text: '#721c24', label: 'Overdue' }
    };
    
    const config = statusConfig[status] || { bg: '#e2e3e5', text: '#383d41', label: status };
    
    return (
      <span className={`payment-badge payment-${status}`} style={{
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 600,
        backgroundColor: config.bg,
        color: config.text
      }}>
        {config.label}
      </span>
    );
  };

  // Filter transactions based on search term
  const filteredTransactions = transactions.filter(transaction => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const productInfo = getProductInfo(transaction);
    const warehouseInfo = getWarehouseInfo(transaction);
    const customerInfo = getCustomerInfo(transaction);
    
    return (
      (transaction.reference_id && transaction.reference_id.toLowerCase().includes(searchLower)) ||
      (transaction.invoice_no && transaction.invoice_no.toLowerCase().includes(searchLower)) ||
      productInfo.name.toLowerCase().includes(searchLower) ||
      productInfo.code.toLowerCase().includes(searchLower) ||
      warehouseInfo.name.toLowerCase().includes(searchLower) ||
      warehouseInfo.code.toLowerCase().includes(searchLower) ||
      customerInfo.name.toLowerCase().includes(searchLower) ||
      customerInfo.code.toLowerCase().includes(searchLower) ||
      (transaction.batch_number && transaction.batch_number.toLowerCase().includes(searchLower)) ||
      (transaction.payment_status && transaction.payment_status.toLowerCase().includes(searchLower))
    );
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTransactions = filteredTransactions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

  const goToCreate = () => {
    navigate("/AddSalesTransaction");
  };

  const goToEdit = (id) => {
    navigate(`/sales/edit/${id}`);
  };

  const goToView = (id) => {
    navigate(`/sales/view/${id}`);
  };

  const handleExport = () => {
    if (filteredTransactions.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const headers = [
        "Ref ID", "Transaction ID", "Invoice No", "Product", "Product Code",
        "Warehouse", "Customer", "Type", "Quantity", "Unit Price",
        "Total Price", "Grand Total", "Transaction Date", "Status",
        "Payment Status", "Amount Paid", "Net Amount", "Batch No", "Is Return", "Created By"
      ];
      
      const csvData = filteredTransactions.map(transaction => {
        const productInfo = getProductInfo(transaction);
        const warehouseInfo = getWarehouseInfo(transaction);
        const customerInfo = getCustomerInfo(transaction);
        const typeInfo = getTransactionTypeInfo(transaction);
        
        return [
          transaction.reference_id || "",
          transaction.transaction_id || "",
          transaction.invoice_no || "",
          productInfo.name,
          productInfo.code,
          warehouseInfo.name,
          customerInfo.name,
          typeInfo.type,
          transaction.quantity || 0,
          transaction.unit_price || 0,
          transaction.total_price || 0,
          transaction.grand_total || 0,
          formatDate(transaction.transaction_date),
          transaction.status || "",
          transaction.payment_status || "",
          transaction.total_amount_paid || 0,
          transaction.net_amount || 0,
          transaction.batch_number || "",
          transaction.is_return ? "Yes" : "No",
          transaction.created_by?.name || transaction.created_by?.Usercomp_sure_name || "System"
        ];
      });

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell => 
          typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
        ).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales-transactions-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success("Export started successfully");
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="animate-spin" size={48} />
        <p>Loading sales transactions...</p>
      </div>
    );
  }

  return (
    <div className="sales-transaction-container">
      {/* Header Section */}
      <div className="header-section">
        <div className="title-section">
          <ShoppingBag size={28} />
          <div>
            <h1>Sales Transactions</h1>
            <p className="company-info">
              {isSystemAdmin ? (
                <>
                  <Globe size={14} /> All Companies
                  {selectedCompany && companies.find(c => c._id === selectedCompany) && (
                    <> - {companies.find(c => c._id === selectedCompany)?.company_name}</>
                  )}
                </>
              ) : (
                <>
                  <Building size={14} /> {company?.company_name} ({company?.company_code})
                </>
              )}
            </p>
          </div>
        </div>
        
        <div className="header-actions">
          <button 
            className="filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          
          <button onClick={handleExport} className="export-btn">
            <Download size={16} /> Export
          </button>
          
          <button onClick={fetchData} className="refresh-btn">
            <RefreshCw size={16} /> Refresh
          </button>
          
          <button onClick={goToCreate} className="create-btn">
            <Plus size={16} /> Create Sale
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">
            <FileText size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.totalTransactions || 0}</span>
            <span className="stat-label">Total Transactions</span>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon green">
            <ShoppingBag size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.saleCount || 0}</span>
            <span className="stat-label">Sales</span>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon orange">
            <RefreshCw size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.returnCount || 0}</span>
            <span className="stat-label">Returns</span>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon purple">
            <Package size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{Math.abs(stats.totalQuantity || 0).toLocaleString()}</span>
            <span className="stat-label">Total Quantity</span>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon teal">
            <DollarSign size={20} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{formatCurrency(stats.totalPrice || 0)}</span>
            <span className="stat-label">Total Sales</span>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-header">
            <Filter size={18} />
            <span>Filter Transactions</span>
            <button onClick={resetFilters} className="clear-filters">
              Clear All
            </button>
          </div>
          
          <div className="filters-grid">
            {/* Company Filter (System Admin Only) */}
            {isSystemAdmin && (
              <div className="filter-group">
                <label>Company</label>
                <div className="select-wrapper">
                  <select
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                  >
                    <option value="">All Companies</option>
                    {companies.map((comp) => (
                      <option key={comp._id} value={comp._id}>
                        {comp.company_name} ({comp.company_code})
                      </option>
                    ))}
                  </select>
                  <Building size={16} className="select-icon" />
                </div>
              </div>
            )}
            
            <div className="filter-group">
              <label>Status</label>
              <div className="select-wrapper">
                <select 
                  name="status" 
                  value={filter.status} 
                  onChange={handleFilterChange}
                >
                  <option value="">All Status</option>
                  <option value="process">Process</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="returned">Returned</option>
                </select>
                <ChevronDown size={16} className="select-icon" />
              </div>
            </div>

            <div className="filter-group">
              <label>Payment Status</label>
              <div className="select-wrapper">
                <select 
                  name="payment_status" 
                  value={filter.payment_status} 
                  onChange={handleFilterChange}
                >
                  <option value="">All Payment Status</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="partial">Partial</option>
                  <option value="notpaid">Not Paid</option>
                  <option value="overdue">Overdue</option>
                </select>
                <ChevronDown size={16} className="select-icon" />
              </div>
            </div>

            <div className="filter-group">
              <label>Transaction Type</label>
              <div className="select-wrapper">
                <select 
                  name="is_return" 
                  value={filter.is_return} 
                  onChange={handleFilterChange}
                >
                  <option value="">All Types</option>
                  <option value="false">Sale</option>
                  <option value="true">Return</option>
                </select>
                <ArrowLeftRight size={16} className="select-icon" />
              </div>
            </div>

            <div className="filter-group">
              <label>Warehouse</label>
              <div className="select-wrapper">
                <select 
                  name="warehouse_id" 
                  value={filter.warehouse_id} 
                  onChange={handleFilterChange}
                >
                  <option value="">All Warehouses</option>
                  {warehouses.map(warehouse => (
                    <option key={warehouse._id} value={warehouse._id}>
                      {warehouse.warehouse_code} - {warehouse.warehouse_name}
                    </option>
                  ))}
                </select>
                <Warehouse size={16} className="select-icon" />
              </div>
            </div>

            <div className="filter-group">
              <label>Customer</label>
              <div className="select-wrapper">
                <select 
                  name="customer_id" 
                  value={filter.customer_id} 
                  onChange={handleFilterChange}
                >
                  <option value="">All Customers</option>
                  {customers.map(customer => (
                    <option key={customer._id} value={customer._id}>
                      {customer.customer_code} - {customer.customer_name}
                    </option>
                  ))}
                </select>
                <Users size={16} className="select-icon" />
              </div>
            </div>

            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                name="start_date"
                value={filter.start_date}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                name="end_date"
                value={filter.end_date}
                onChange={handleFilterChange}
              />
            </div>
          </div>

          <div className="filters-actions">
            <button onClick={applyFilters} className="apply-filters">
              <Filter size={16} /> Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Search Section */}
      <div className="search-section">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by reference, product, warehouse, customer, payment status..."
            value={searchTerm}
            onChange={handleSearch}
          />
          {searchTerm && (
            <button 
              className="clear-search"
              onClick={() => setSearchTerm("")}
            >
              <X size={14} />
            </button>
          )}
        </div>
        
        <div className="search-info">
          Showing {filteredTransactions.length} of {transactions.length} transactions
        </div>
      </div>

      {/* Transactions Table */}
      <div className="table-container">
        <table className="transactions-table">
          <thead>
            <tr>
              <th>Ref ID</th>
              <th>Product</th>
              <th>Warehouse</th>
              <th>Customer</th>
              <th>Quantity</th>
              <th>Unit Price</th>
              <th>Total Price</th>
              <th>Date</th>
              <th>Status</th>
              <th>Payment</th>
            </tr>
          </thead>
          <tbody>
            {currentTransactions.length === 0 ? (
              <tr>
                <td colSpan="11" className="no-data">
                  <div className="empty-state">
                    <ShoppingBag size={48} color="#ccc" />
                    <p>
                      {transactions.length === 0
                        ? "No sales transactions found. Create your first sales transaction!"
                        : "No transactions match your search criteria."
                      }
                    </p>
                    {transactions.length === 0 && (
                      <button 
                        className="create-btn" 
                        onClick={goToCreate}
                      >
                        <Plus size={16} /> Create First Sale
                      </button>
                    )}
                    {transactions.length > 0 && (
                      <button 
                        className="clear-filters-btn"
                        onClick={resetFilters}
                      >
                        Clear Filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              currentTransactions.map((transaction) => {
                const productInfo = getProductInfo(transaction);
                const warehouseInfo = getWarehouseInfo(transaction);
                const customerInfo = getCustomerInfo(transaction);
                const typeInfo = getTransactionTypeInfo(transaction);
                const isReturn = transaction.is_return || typeInfo.sign === '+';
                const balance = (transaction.grand_total || 0) - (transaction.total_amount_paid || 0);
                
                return (
                  <tr key={transaction._id} className={isReturn ? 'return-row' : ''}>
                    <td>
                      <div className="reference-id">
                        <span className="ref-id-badge">
                          {transaction.reference_id || `TX${transaction.transaction_id}`}
                        </span>
                        <span className="ref-id-sub">
                          {transaction.invoice_no}
                        </span>
                        {isReturn && (
                          <span className="return-badge">RETURN</span>
                        )}
                      </div>
                    </td>
                    
                    <td>
                      <div className="product-info">
                        <Package size={14} />
                        <div>
                          <div className="product-name">{productInfo.name}</div>
                          <div className="product-code">{productInfo.code}</div>
                        </div>
                      </div>
                    </td>
                    
                    <td>
                      <div className="warehouse-info">
                        <Warehouse size={14} />
                        <div>
                          <div className="warehouse-name">{warehouseInfo.name}</div>
                          <div className="warehouse-code">{warehouseInfo.code}</div>
                        </div>
                      </div>
                    </td>
                    
                    <td>
                      <div className="customer-info">
                        <User size={14} />
                        <div>
                          <div className="customer-name">{customerInfo.name}</div>
                          <div className="customer-code">{customerInfo.code}</div>
                        </div>
                      </div>
                    </td>
                    
                    <td>
                      <span className={`quantity ${isReturn ? 'negative' : 'positive'}`}>
                        {typeInfo.sign}{Math.abs(transaction.quantity || 0).toLocaleString()}
                      </span>
                    </td>
                    
                    <td className="total-cost">{formatCurrency(transaction.unit_price)}</td>
                    
                    <td className="total-cost">{formatCurrency(transaction.total_price)}</td>
                    
                    <td>{formatDate(transaction.transaction_date)}</td>
                    
                    <td>
                      {getStatusBadge(transaction.status)}
                    </td>
                    
                    <td>
                      <div>
                        {getPaymentStatusBadge(transaction.payment_status)}
                        
                        {/* Payment details */}
                        <div style={{ 
                          marginTop: '4px', 
                          fontSize: '10px',
                          background: '#f9f9f9',
                          padding: '4px',
                          borderRadius: '4px'
                        }}>
                          <div><strong>Paid:</strong> {formatCurrency(transaction.total_amount_paid || 0)}</div>
                          <div>
                            <strong>Balance:</strong>{' '}
                            <span style={{ color: balance > 0 ? '#ff4d4f' : '#52c41a' }}>
                              {formatCurrency(balance)}
                            </span>
                          </div>
                          
                          {/* Show payment methods if available */}
                          {transaction.payment_details && transaction.payment_details.length > 0 && (
                            <div style={{ marginTop: '2px', borderTop: '1px dashed #ccc', paddingTop: '2px' }}>
                              {transaction.payment_details.map((payment, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                  {getPaymentMethodIcon(payment.method)}
                                  <span>{payment.method}: {formatCurrency(payment.amount)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              <ChevronLeft size={16} />
              Previous
            </button>
            
            <div className="page-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
              <ChevronRight size={16} />
            </button>
            
            <div className="pagination-info">
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredTransactions.length)} of {filteredTransactions.length} entries
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesTransactionList;